/**
 * FlowCanvas - SVG container for the flow visualization
 *
 * Provides the responsive SVG canvas with proper viewBox sizing.
 * Uses pure CSS for responsive behavior — no JavaScript ResizeObserver,
 * no React state updates, no feedback loops. (WP-BUG-54 v2)
 */

import { memo } from 'react';
import type { FlowCanvasProps } from './types';
import { CANVAS, RESPONSIVE, COLUMNS, NODE } from './constants';
import { cn } from '@/utils/cn';

/**
 * Extended props for FlowCanvas with accessibility metadata
 */
interface FlowCanvasExtendedProps extends FlowCanvasProps {
  /** Period label for accessibility description (e.g., "November 2024") */
  periodLabel?: string;
  /** Total income for the period */
  totalIncome?: number;
  /** Total expenses for the period */
  totalExpenses?: number;
}

/**
 * Format currency for accessibility text (no symbols, just numbers)
 */
function formatAccessibleCurrency(amount: number): string {
  return `$${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function FlowCanvasComponent({
  children,
  width = CANVAS.WIDTH,
  height = CANVAS.HEIGHT,
  className,
  periodLabel,
  totalIncome = 0,
  totalExpenses = 0,
}: FlowCanvasExtendedProps) {
  // Build accessibility description
  const netFlow = totalIncome - totalExpenses;
  const accessibleDescription = periodLabel
    ? `Flow Visualization for ${periodLabel}. Income: ${formatAccessibleCurrency(totalIncome)}. Expenses: ${formatAccessibleCurrency(totalExpenses)}. Net: ${formatAccessibleCurrency(netFlow)}.`
    : 'Money flow diagram showing income sources flowing through accounts to expenses.';

  return (
    <div
      className={cn(
        'flow-canvas-container relative overflow-x-auto',
        className
      )}
    >
      {/* Min-width wrapper ensures diagram is always readable.
          overflow-x-auto on the container only shows a scrollbar when
          the container is narrower than MIN_CANVAS_WIDTH — no JS needed. */}
      <div style={{ minWidth: RESPONSIVE.MIN_CANVAS_WIDTH }}>
        <svg
          className="flow-canvas"
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label="Money flow diagram showing income sources, accounts, and expenses"
        >
          {/* Accessibility: Title and description for screen readers */}
          <title>
            {periodLabel ? `Flow Visualization for ${periodLabel}` : 'Flow Visualization'}
          </title>
          <desc>{accessibleDescription}</desc>

          {/* SVG Definitions for filters and gradients */}
          <defs>
            {/* Subtle drop shadow for nodes */}
            <filter id="node-shadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="1" dy="1" stdDeviation="2" floodOpacity="0.15" />
            </filter>

            {/* Slightly stronger shadow for hover states */}
            <filter id="node-shadow-hover" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="1" dy="2" stdDeviation="3" floodOpacity="0.2" />
            </filter>
          </defs>

          {/* Background */}
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            fill="transparent"
          />

          {/* Column labels - centered over respective node columns */}
          <text
            x={COLUMNS.INFLOW_X + NODE.WIDTH / 2}
            y={28}
            textAnchor="middle"
            className="layer-label"
            fill="#757575"
            fontSize={14}
            fontWeight={600}
            style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            Inflows
          </text>
          <text
            x={COLUMNS.ACCOUNT_X + NODE.ACCOUNT_WIDTH / 2}
            y={28}
            textAnchor="middle"
            className="layer-label"
            fill="#757575"
            fontSize={14}
            fontWeight={600}
            style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            Accounts
          </text>
          <text
            x={COLUMNS.OUTFLOW_X + NODE.WIDTH / 2}
            y={28}
            textAnchor="middle"
            className="layer-label"
            fill="#757575"
            fontSize={14}
            fontWeight={600}
            style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            Outflows
          </text>

          {/* Children (edges, nodes, etc.) */}
          {children}
        </svg>
      </div>
    </div>
  );
}

export const FlowCanvas = memo(FlowCanvasComponent);
export default FlowCanvas;
